// src/components/ResumeProfile.js
import React, { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import '../pages/resume-styles.css';

import ResumeUpload from './ui/ResumeUpload';
import ResumeForm from './ui/ResumeForm';
import PDFViewerProfile from './ui/PDFViewerProfile';
import SuccessNotification from './ui/SuccessNotification';
import { emptyResumeData } from '../utils/resumeData';
import {
    saveResumeProfile,
    getResumeProfile,
    deleteResumeProfile
} from '../utils/resumeProfileAPI';
import {
    uploadResumePdf,
    getResumeMetadata,
    downloadResumeUrl,
    deleteUploadedResume,
} from "../utils/resumeUploadAPI";

/** Nice JobQUE-styled delete confirmation modal */
function DeleteProfileModal({ onConfirm, onCancel, isDeleting }) {
    return (
        <div className="delete-modal-backdrop">
            <div className="delete-modal-card animate-fade-in-up">
                <div className="delete-modal-icon">
                    <Trash2 className="w-6 h-6" />
                </div>

                <h3 className="delete-modal-title">Delete resume profile?</h3>
                <p className="delete-modal-text">
                    This will remove your saved resume data from JobQUE.ai and disconnect it
                    from the browser extension. This action cannot be undone.
                </p>

                <div className="delete-modal-actions">
                    <button
                        type="button"
                        className="delete-modal-cancel-btn"
                        onClick={onCancel}
                        disabled={isDeleting}
                    >
                        Keep Profile
                    </button>
                    <button
                        type="button"
                        className="delete-modal-delete-btn"
                        onClick={onConfirm}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'DeletingΓÇª' : 'Delete Profile'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ResumeProfile({ userEmail }) {
    const [resumeData, setResumeData] = useState({ ...emptyResumeData });
    const [showSuccess, setShowSuccess] = useState(false);
    const [hasProfile, setHasProfile] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfFileName, setPdfFileName] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [pdfExpiresAt, setPdfExpiresAt] = useState(null);

    // Load profile from backend when component mounts
    useEffect(() => {
        if (!userEmail) return;

        const loadProfile = async () => {
            setIsLoading(true);

            try {
                // First check for temp data from Extension page
                const tempData = localStorage.getItem('temp_resume_data');
                if (tempData) {
                    try {
                        const parsed = JSON.parse(tempData);
                        setResumeData(parsed);
                        setHasProfile(true);
                        setIsEditing(false);

                        // Save temp data to backend
                        const saveResult = await saveResumeProfile(userEmail, parsed);

                        if (saveResult.success) {
                            // Update local storage with user-specific key
                            const storageKey = `jobque_resume_data_${userEmail}`;
                            localStorage.setItem(storageKey, JSON.stringify(parsed));
                            localStorage.removeItem('temp_resume_data');

                            setSuccessMessage('Resume profile saved from your extension form!');
                            setShowSuccess(true);
                        }
                    } catch (error) {
                        console.error('Error processing temp resume data:', error);
                    }
                    setIsLoading(false);
                    return;
                }

                // Load from backend
                const result = await getResumeProfile(userEmail);

                if (result.success && result.hasProfile) {
                    setResumeData(result.data);
                    setHasProfile(true);

                    // Update local storage
                    const storageKey = `jobque_resume_data_${userEmail}`;
                    localStorage.setItem(storageKey, JSON.stringify(result.data));
                } else {
                    // No profile found - check local storage as fallback
                    const storageKey = `jobque_resume_data_${userEmail}`;
                    const localData = localStorage.getItem(storageKey);

                    if (localData) {
                        try {
                            const parsed = JSON.parse(localData);
                            setResumeData(parsed);
                            setHasProfile(true);
                        } catch (error) {
                            console.error('Error parsing local resume data:', error);
                            setHasProfile(false);
                        }
                    } else {
                        setHasProfile(false);
                    }
                }
            } catch (error) {
                console.error('Error loading resume profile:', error);
                setHasProfile(false);
            } finally {
                setIsLoading(false);
            }
        };

        loadProfile();
    }, [userEmail]);

    useEffect(() => {
        if (!userEmail) return;

        (async () => {
            const meta = await getResumeMetadata(userEmail);
            if (meta.success && meta.hasResume) {
                await refreshPdfFromBackend();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userEmail]);

    const refreshPdfFromBackend = async () => {
        if (!userEmail) return;

        const dl = await downloadResumeUrl(userEmail);

        if (dl.success && dl.hasResume) {
            // Revoke old blob URLs to avoid leaks
            if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
                URL.revokeObjectURL(pdfUrl);
            }

            setPdfUrl(dl.downloadUrl);
            setPdfFileName(dl.fileName || "");
            setPdfExpiresAt(Date.now() + (dl.expiresIn || 300) * 1000);
        } else {
            setPdfExpiresAt(null);
        }
    };

    useEffect(() => {
        if (!userEmail || !pdfExpiresAt) return;

        // Refresh 10s before expiry (URL is valid ~5 min)
        const msUntilRefresh = Math.max(pdfExpiresAt - Date.now() - 10000, 5000);
        const t = setTimeout(() => refreshPdfFromBackend(), msUntilRefresh);

        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pdfExpiresAt, userEmail]);

    const handleFileSelect = async (file) => {
        // Show preview instantly
        if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
            URL.revokeObjectURL(pdfUrl);
        }
        const localUrl = URL.createObjectURL(file);
        setPdfUrl(localUrl);
        setPdfFileName(file.name);
        setPdfExpiresAt(null);

        // Upload to backend
        const up = await uploadResumePdf(userEmail, file);

        if (up.success) {
            // Swap preview to presigned URL and start expiry refresh loop
            await refreshPdfFromBackend();
        } else {
            console.error("Resume upload failed:", up.message);
            // optional: show your UI alert/toast
            alert(up.message || "Failed to upload resume PDF.");
        }
    };

    const handleSave = async () => {
        setIsSaving(true);

        try {
            // Compute full name from firstName and lastName
            const fullName = `${resumeData.firstName || ''} ${resumeData.lastName || ''}`.trim();

            // Create updated resume data with full name
            const updatedResumeData = {
                ...resumeData,
                fullName: fullName
            };

            const userEmail = localStorage.getItem('userEmail');

            // Save to backend with full name included
            const result = await saveResumeProfile(userEmail, updatedResumeData);

            if (!result.success) {
                throw new Error(result.message);
            }

            // Save to local storage with full name
            const storageKey = `jobque_resume_data_${userEmail}`;
            localStorage.setItem(storageKey, JSON.stringify(updatedResumeData));

            // Send to extension with full name
            window.postMessage(
                { type: 'RESUME_AUTOFILL_SAVE', payload: updatedResumeData },
                '*'
            );

            // Update local state with full name
            setResumeData(updatedResumeData);
            setHasProfile(true);
            setIsEditing(false);
            setSuccessMessage('Resume profile saved successfully!');
            setShowSuccess(true);
        } catch (error) {
            console.error('Error saving resume data:', error);
            setSuccessMessage('Failed to save resume profile. Please try again.');
            setShowSuccess(true);
        } finally {
            setIsSaving(false);
        }
    };

    const requestDelete = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        setIsDeleting(true);

        try {
            // 1) Delete resume profile (backend)
            const result = await deleteResumeProfile(userEmail);
            if (!result.success) throw new Error(result.message);

            // 2) ALSO delete uploaded resume PDF (new requirement)
            // If it doesn't exist, your API helper should treat 404 as success
            await deleteUploadedResume(userEmail);

            // 3) Clear frontend state
            setHasProfile(false);
            setIsEditing(true);

            // Clear form fields (Γ£à you use resumeData, not formData)
            setResumeData({ ...emptyResumeData });

            // Clear PDF preview
            if (pdfUrl && String(pdfUrl).startsWith("blob:")) {
                URL.revokeObjectURL(pdfUrl);
            }
            setPdfUrl(null);
            setPdfFileName("");
            setPdfExpiresAt(null);

            // Close the delete modal (Γ£à your state is showDeleteConfirm)
            setShowDeleteConfirm(false);

            // Show success
            setSuccessMessage("Resume profile and uploaded resume deleted successfully.");
            setShowSuccess(true);
        } catch (err) {
            console.error("Delete error:", err);
            setShowDeleteConfirm(false);
            alert("Failed to delete resume profile. Please try again.");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    const handleEdit = () => setIsEditing(true);

    const handleCancel = () => {
        const storageKey = `jobque_resume_data_${userEmail}`;
        const savedData = localStorage.getItem(storageKey);
        if (savedData) {
            try {
                setResumeData(JSON.parse(savedData));
            } catch (e) {
                console.error('Error restoring saved resume data:', e);
            }
        }
        setIsEditing(false);
    };

    // Show loading state
    if (isLoading) {
        return (
            <div className="resume-profile-view" style={{ margin: '0 auto', padding: '0 40px' }}>
                <div className="resume-profile-header">
                    <h2 className="text-3xl font-bold text-[var(--charcoal-black)] mb-6">
                        Loading Resume Profile...
                    </h2>
                </div>
            </div>
        );
    }

    /* ==========================
     * VIEW MODE (table layout)
     * ========================== */
    if (hasProfile && !isEditing) {
        return (
            <div
                className="resume-profile-view"
                style={{ margin: '0 auto', padding: '0 40px' }}
            >
                {showDeleteConfirm && (
                    <DeleteProfileModal
                        onConfirm={handleDeleteConfirm}
                        onCancel={handleDeleteCancel}
                        isDeleting={isDeleting}
                    />
                )}

                {showSuccess && (
                    <SuccessNotification
                        message={successMessage}
                        onClose={() => setShowSuccess(false)}
                    />
                )}

                <div className="resume-profile-header">
                    <h2 className="text-3xl font-bold text-[var(--charcoal-black)] mb-6">
                        Your Resume Profile
                    </h2>

                    <div className="resume-profile-actions">
                        <button
                            onClick={handleEdit}
                            className="px-6 py-3 bg-[var(--forest-green)] text-white font-semibold rounded-full hover:bg-[var(--deep-teal)] transition-all shadow-md hover:shadow-lg cursor-pointer"
                        >
                            Edit Profile
                        </button>
                        <button
                            onClick={requestDelete}
                            className="px-6 py-3 text-white font-semibold rounded-full transition-all shadow-md hover:shadow-lg flex items-center gap-2 danger-button cursor-pointer"
                        >
                            <Trash2 className="w-4 h-4" />
                            Delete Profile
                        </button>
                    </div>
                </div>

                <div className="resume-data-table">
                    {/* Basic Info */}
                    <div className="resume-section">
                        <h3 className="resume-section-title">Basic Information</h3>
                        <div className="resume-data-grid">
                            <div className="resume-data-row">
                                <span className="resume-data-label">First Name:</span>
                                <span className="resume-data-value">
                                    {resumeData.firstName || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">Last Name:</span>
                                <span className="resume-data-value">
                                    {resumeData.lastName || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">Email:</span>
                                <span className="resume-data-value">
                                    {resumeData.email || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">Phone:</span>
                                <span className="resume-data-value">
                                    {resumeData.phone || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">Address:</span>
                                <span className="resume-data-value">
                                    {resumeData.address || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">City:</span>
                                <span className="resume-data-value">
                                    {resumeData.city || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">State:</span>
                                <span className="resume-data-value">
                                    {resumeData.state || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">LinkedIn:</span>
                                <span className="resume-data-value">
                                    {resumeData.linkedin ? (
                                        <a
                                            href={resumeData.linkedin}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-[var(--forest-green)] hover:underline"
                                        >
                                            {resumeData.linkedin}
                                        </a>
                                    ) : (
                                        'Not provided'
                                    )}
                                </span>
                            </div>
                            <div className="resume-data-row resume-data-full">
                                <span className="resume-data-label">Summary:</span>
                                <span className="resume-data-value">
                                    {resumeData.summary || 'Not provided'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="resume-section">
                        <h3 className="resume-section-title">Additional Information</h3>
                        <div className="resume-data-grid">
                            <div className="resume-data-row">
                                <span className="resume-data-label">Disability Status:</span>
                                <span className="resume-data-value">
                                    {resumeData.disabilityStatus || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">Veteran Status:</span>
                                <span className="resume-data-value">
                                    {resumeData.veteranStatus || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">Race/Ethnicity:</span>
                                <span className="resume-data-value">
                                    {resumeData.raceEthnicity || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">Gender:</span>
                                <span className="resume-data-value">
                                    {resumeData.gender || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">
                                    Sponsorship Required:
                                </span>
                                <span className="resume-data-value">
                                    {resumeData.sponsorshipRequired || 'Not provided'}
                                </span>
                            </div>
                            <div className="resume-data-row">
                                <span className="resume-data-label">Work Authorization:</span>
                                <span className="resume-data-value">
                                    {resumeData.workAuthorization || 'Not provided'}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Skills */}
                    <div className="resume-section">
                        <h3 className="resume-section-title">Skills</h3>
                        <div className="resume-skills-list">
                            {resumeData.skills && resumeData.skills.length > 0 ? (
                                resumeData.skills.map((skill, index) => (
                                    <span key={index} className="resume-skill-tag">
                                        {skill}
                                    </span>
                                ))
                            ) : (
                                <span className="resume-data-value">No skills added</span>
                            )}
                        </div>
                    </div>

                    {/* Experience */}
                    <div className="resume-section">
                        <h3 className="resume-section-title">Work Experience</h3>
                        {resumeData.experience && resumeData.experience.length > 0 ? (
                            resumeData.experience.map((exp, index) => (
                                <div key={index} className="resume-experience-item">
                                    <h4 className="resume-experience-title">
                                        {exp.title || 'Position not specified'}
                                    </h4>
                                    <p className="resume-experience-company">
                                        {exp.company || 'Company not specified'}
                                    </p>
                                    <p className="resume-experience-dates">
                                        {exp.startDate || 'Start date not specified'} -{' '}
                                        {exp.endDate || 'End date not specified'}
                                    </p>
                                    <p className="resume-experience-description">
                                        {exp.description || 'No description provided'}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <span className="resume-data-value">
                                No work experience added
                            </span>
                        )}
                    </div>

                    {/* Education */}
                    <div className="resume-section">
                        <h3 className="resume-section-title">Education</h3>
                        {resumeData.education && resumeData.education.length > 0 ? (
                            resumeData.education.map((edu, index) => (
                                <div key={index} className="resume-education-item">
                                    <h4 className="resume-education-degree">
                                        {edu.degree || 'Degree not specified'}
                                    </h4>
                                    <p className="resume-education-field">
                                        {edu.fieldOfStudy || 'Field not specified'}
                                    </p>
                                    <p className="resume-education-school">
                                        {edu.school || 'School not specified'}
                                    </p>
                                    <p className="resume-education-date">
                                        {edu.graduationDate || 'Graduation date not specified'}
                                    </p>
                                </div>
                            ))
                        ) : (
                            <span className="resume-data-value">No education added</span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    /* ==========================
     * EDIT MODE (matches Extension layout)
     * ========================== */
    return (
        <div className="resume-extension-page min-h-screen py-12 px-4">
            {showSuccess && (
                <SuccessNotification
                    message={successMessage}
                    onClose={() => setShowSuccess(false)}
                />
            )}

            <div className="mx-auto w-full max-w-none">
                <div id="resume-form" className="mt-4 md:mt-8">
                    <div className="text-center mb-8 animate-fade-in-up">
                        <h2 className="text-3xl md:text-4xl font-bold text-[var(--forest-green)] mb-4 font-poppins">
                            {hasProfile ? 'Edit Your Resume Profile' : 'Your Resume Profile'}
                        </h2>
                        <p className="text-lg text-[#4B5563]">
                            Fill out your information once and never type it again
                        </p>
                    </div>

                    <div className="flex flex-col lg:flex-row gap-8 items-start relative">
                        <PDFViewerProfile
                            pdfUrl={pdfUrl}
                            fileName={pdfFileName}
                            scrollTargetId="resume-form"
                            headerOffset={200}
                        />

                        <div className="w-full lg:w-1/2 xl:w-1/2 2xl:w-1/2 lg:ml-auto">
                            <div className="bg-white rounded-3xl shadow-xl p-6 md:p-8">
                                <ResumeUpload onFileSelect={handleFileSelect} />
                                <ResumeForm
                                    data={resumeData}
                                    onChange={setResumeData}
                                    onSave={handleSave}
                                    isSaving={isSaving}
                                />

                                {hasProfile && (
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        disabled={isSaving}
                                        className="w-full mt-4 py-4 bg-white text-[var(--charcoal-black)] border-2 border-[var(--charcoal-black)] font-semibold text-lg rounded-full hover:bg-[var(--charcoal-black)] hover:text-white transition-all shadow-lg hover:shadow-xl cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
